import type {
  AddressChangedResult,
  AuthorizeExpressPaymentRequestModel,
  EswPaymentsIntegrationService,
  ExpressPaymentApprovalRequest,
  ExpressPaymentAttempt,
  SessionResult,
  ShippingOptionChangeRequest
} from '@esw/payments-express-panel';
import { initializeFraudBootstrap } from '../core/fraud-manager';
import { logger } from '../core/logger';
import {
  ExpressPaymentComponentConfig,
  ExpressPaymentComponentInstance,
  ExpressPaymentEventMap,
  ExpressPaymentOutputEvent
} from '../types/external/express-payment-component';
import { persistAndAuthoriseExpressPayment, validateAddressApi, PartialAddress } from './utils/express-payment-api';
import { getModuleBaseUrlOrThrow } from '../core/script-base-url';
import { environment } from '../environments/environment';
import { loadExpressPaymentBundle } from './utils/web-component-loader';
import {
  ESW_ERROR_CODES,
  ESWAPIError,
  ESWConfigurationError,
  ESWConnectionError,
  ESWError,
  ESWPaymentError,
  ESWStateError,
  ESWValidationError,
  serializeErrorEvent
} from './utils/error-handling';

const {
  EVENT_HANDLER_ERROR,
  MOUNT_ABORTED,
  SUBSCRIPTION_ERROR,
  MOUNT_FAILED,
  INVALID_SELECTOR,
  MISSING_CONFIG_PARAM,
  FORTER_TOKEN_UNAVAILABLE,
  COMPONENT_INITIALIZATION_TIMEOUT,
  ADDRESS_VALIDATION_FAILED,
  INTEGRATION_SERVICE_UNAVAILABLE,
  AUTHORIZE_PAYMENT_FAILED,
  COMPONENT_NOT_MOUNTED
} = ESW_ERROR_CODES;

const paymentContainerClass = 'express-payment-container';

function mountExpressPaymentContainer(selector: string): HTMLDivElement {
  const targetElement = document.querySelector(selector);

  if (!targetElement) {
    logger.error(`Element with selector ${selector} not found`);
    throw new ESWConfigurationError(`Element with selector ${selector} not found`, INVALID_SELECTOR, selector);
  }

  const container = document.createElement('div');
  container.className = paymentContainerClass;
  targetElement.appendChild(container);

  return container;
}

function unmountExpressPaymentContainer(container: HTMLDivElement | null): void {
  container?.remove();
}

export function getPaymentsPanelUrl(): string {
  return environment.paymentsPanelApiUrl;
}

export function getComposableApiUrl(): string {
  return `${environment.composableApiUrl}/composable/v1`;
}

/**
 * Creates an express payment component with methods to mount, update, and unmount
 *
 * This orchestrator handles:
 * - Loading the @esw/payments-express-panel web component
 * - Managing component lifecycle (mount, update, unmount)
 * - Event name normalization (maps Angular events to external API)
 * - DOM management
 */
type EventListenerStore = {
  [E in ExpressPaymentOutputEvent]?: (data: ExpressPaymentEventMap[E]) => void;
};

const createEventStore = (): EventListenerStore => ({
  ready: undefined,
  status_changed: undefined,
  address_changed: undefined,
  shipping_option_changed: undefined,
  payment_approval_request: undefined,
  payment_attempt: undefined,
  payment_session_result: undefined,
  error: undefined
});

export const triggerEvent = <E extends ExpressPaymentOutputEvent>(store: EventListenerStore, eventName: E, data: unknown): void => {
  const callback = store[eventName];
  if (callback) {
    try {
      // Serialize error objects before passing to integrator
      if (eventName === 'error') {
        callback(serializeErrorEvent(data) as ExpressPaymentEventMap[E]);
      } else {
        callback(data as ExpressPaymentEventMap[E]);
      }
    } catch (error) {
      logger.error(`Error in ${eventName} event listener:`, error);

      // Emit error event if the failing event isn't already the error event
      if (eventName !== 'error') {
        const wrappedError = new ESWStateError(
          `Integrator's ${eventName} handler threw an error: ${error instanceof Error ? error.message : String(error)}`,
          EVENT_HANDLER_ERROR
        );
        triggerEvent(store, 'error', { error: wrappedError, component: 'express-payment' });
      }
    }
  }
};

export function configureExpressPaymentComponent(config: ExpressPaymentComponentConfig): ExpressPaymentComponentInstance {
  const initialState = {
    eventListeners: createEventStore(),
    mounted: false,
    element: null as HTMLElement | null,
    container: null as HTMLDivElement | null,
    selectedShippingOptionId: null as string | null,
    integrationService: null as EswPaymentsIntegrationService | null,
    addressChangedSubscription: null as { unsubscribe: () => void } | null,
    shippingOptionChangedSubscription: null as { unsubscribe: () => void } | null,
    paymentAttemptSubscription: null as { unsubscribe: () => void } | null,
    paymentSessionResultSubscription: null as { unsubscribe: () => void } | null,
    setupPaymentAuthorisationSubscription: null as { unsubscribe: () => void } | null,
    eventListenersAbortController: null as AbortController | null
  };

  let state = { ...initialState };

  const cleanupResources = (): void => {
    state.eventListenersAbortController?.abort();
    if (state.element) {
      state.element.remove();
    }
    state.addressChangedSubscription?.unsubscribe();
    state.shippingOptionChangedSubscription?.unsubscribe();
    state.paymentAttemptSubscription?.unsubscribe();
    state.paymentSessionResultSubscription?.unsubscribe();
    state.setupPaymentAuthorisationSubscription?.unsubscribe();
    unmountExpressPaymentContainer(state.container);
    state = { ...initialState };
    logger.info('Express payment component resources cleaned up');
  };

  /**
   * Creates an error handler for RxJS subscriptions.
   * Logs the error and emits it through the error event channel.
   */
  const createSubscriptionErrorHandler =
    (streamName: string) =>
    (err: unknown): void => {
      logger.error(`${streamName} observable errored:`, err);
      const error = new ESWStateError(`${streamName} stream failed`, SUBSCRIPTION_ERROR);
      triggerEvent(state.eventListeners, 'error', { error, component: 'express-payment' });
    };

  /**
   * Handles address validation errors
   */
  const handleAddressValidationError = (error: unknown, eventListeners: EventListenerStore): Record<string, string[]> => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const normalizedError =
      error instanceof ESWError
        ? error
        : new ESWStateError(`Unexpected error in address validation: ${errorMessage}`, ADDRESS_VALIDATION_FAILED);

    if (error instanceof ESWError) {
      logger.error(`${error.type}:`, error.toJSON());
    } else {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('Error during address validation:', errorMessage, error);
    }

    triggerEvent(eventListeners, 'error', { error: normalizedError, component: 'express-payment' });

    return error instanceof ESWValidationError && error.fields
      ? error.fields
      : { general: ['Address validation failed. Please try again.'] };
  };

  /**
   * Sets up internal event listeners that map to external events
   */
  const setupEventListeners = (el: HTMLElement): AbortController => {
    const controller = new AbortController();

    el.addEventListener(
      'expressPaymentPanelInitialised',
      () => {
        triggerEvent(state.eventListeners, 'ready', undefined);
      },
      { signal: controller.signal }
    );

    el.addEventListener(
      'statusChanged',
      ((event: CustomEvent) => {
        triggerEvent(state.eventListeners, 'status_changed', event.detail);
      }) as EventListener,
      { signal: controller.signal }
    );

    return controller;
  };

  return {
    mount: async (selector: string): Promise<void> => {
      if (state.mounted) {
        cleanupResources();
      }

      const angularAbortController = new AbortController();

      try {
        const newContainer = mountExpressPaymentContainer(selector);

        await initializeFraudBootstrap();
        const forterToken = globalThis.FRAUDBOOTSTRAP?.getForterToken();

        if (!forterToken) {
          logger.warn('Forter token not available from FraudBootstrap - payment authorization may fail');

          // Notify integrator that fraud token is missing
          triggerEvent(state.eventListeners, 'error', {
            error: new ESWStateError(
              'Fraud prevention token (Forter) is unavailable. Payment authorization may be rejected by the backend.',
              FORTER_TOKEN_UNAVAILABLE
            ),
            component: 'express-payment'
          });
        }

        const baseUrl = getModuleBaseUrlOrThrow();
        const paymentsPanelUrl = getPaymentsPanelUrl();

        // IMPORTANT: Load polyfills first (contains Zone.js required by Angular)
        const { defineExpressPaymentElement } = await loadExpressPaymentBundle(baseUrl);

        await defineExpressPaymentElement({
          environment: {
            paymentsPanelUrl
          }
        });

        await customElements.whenDefined('esw-express-payment-panel');

        const newElement = document.createElement('esw-express-payment-panel') as HTMLElement & {
          userData: unknown;
          instrumentationKey?: string;
          integrationService?: EswPaymentsIntegrationService;
        };

        // Set userData and instrumentationKey as properties
        newElement.userData = config.userData;
        const { tenantCode, expressPaymentId } = config.userData;

        if (!tenantCode || !expressPaymentId) {
          throw new ESWConfigurationError(
            'Express payment panel config.userData must include tenantCode and expressPaymentId',
            MISSING_CONFIG_PARAM
          );
        }

        newElement.instrumentationKey = environment.appInsights;

        // Set up event listeners before appending to DOM
        const eventListenersAbortController = setupEventListeners(newElement);
        state.eventListenersAbortController = eventListenersAbortController;

        // Create a promise that resolves when Angular is initialized via the event listener sent from express-panel web component
        const angularReady = new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(
            () =>
              reject(
                new ESWStateError(
                  'Express payment panel initialization timeout - component did not connect within 5 seconds',
                  COMPONENT_INITIALIZATION_TIMEOUT
                )
              ),
            5000
          );

          // Cleanup function
          angularAbortController.signal.addEventListener('abort', () => {
            clearTimeout(timeout);
            reject(new ESWStateError('Mount aborted', MOUNT_ABORTED));
          });

          newElement.addEventListener(
            'expressPaymentPanelConnected',
            () => {
              logger.info('Express payment panel connected');
              clearTimeout(timeout);
              resolve();
            },
            { once: true, signal: angularAbortController.signal }
          );
        });

        newContainer.appendChild(newElement);

        // Wait for Angular to signal it's ready
        await angularReady;

        // integrationService is guaranteed to be available after initialization
        const integrationService = newElement.integrationService;
        if (!integrationService) {
          throw new ESWStateError('Integration service not available after Angular initialization', INTEGRATION_SERVICE_UNAVAILABLE);
        }

        const paymentAttemptSubscription = integrationService.getPaymentAttempt$.subscribe({
          next: (attempt: ExpressPaymentAttempt) => {
            triggerEvent(state.eventListeners, 'payment_attempt', attempt);
          },
          error: createSubscriptionErrorHandler('Payment attempt')
        });

        const addressChangedSubscription = integrationService.getAddressChangedRequest$.subscribe({
          next: async (address: unknown) => {
            try {
              triggerEvent(state.eventListeners, 'address_changed', address);
              const res = await validateAddressApi(address as PartialAddress, tenantCode, expressPaymentId);

              // check if the previously selected shipping option is still valid
              const hasValidSelection =
                state.selectedShippingOptionId && res.shippingOptions.some((opt) => opt.id === state.selectedShippingOptionId);

              // if shippingOption not valid, select the first available shipping option and notify integrator
              if (!hasValidSelection && res.shippingOptions.length > 0) {
                state.selectedShippingOptionId = res.shippingOptions[0].id;
                integrationService.setShippingOptionChangedResult({
                  price: null,
                  priceBreakdown: null,
                  shippingOptions: res.shippingOptions,
                  errorMessage: null
                });
                triggerEvent(state.eventListeners, 'shipping_option_changed', { shippingOption: res.shippingOptions[0] });
              }

              const shippingOptions = res.shippingOptions.map((opt) => ({ ...opt, selected: opt.id === state.selectedShippingOptionId }));

              integrationService.setAddressChangedResult({
                errors: null,
                shippingOptions
              } as AddressChangedResult);
            } catch (error) {
              const errors = handleAddressValidationError(error, state.eventListeners);
              integrationService.setAddressChangedResult({
                errors,
                shippingOptions: null
              } as unknown as AddressChangedResult);
            }
          },
          error: createSubscriptionErrorHandler('Address validation')
        });

        const shippingOptionChangedSubscription = integrationService.getShippingOptionChangedRequest$.subscribe({
          next: (request: ShippingOptionChangeRequest) => {
            state.selectedShippingOptionId = request.shippingOption.id;
            triggerEvent(state.eventListeners, 'shipping_option_changed', request);
          },
          error: createSubscriptionErrorHandler('Shipping option change')
        });

        const setupPaymentAuthorisationSubscription = integrationService.getExpressPaymentApprovalRequest$.subscribe({
          next: async (req: ExpressPaymentApprovalRequest) => {
            const expressApprovalRequest = req;
            triggerEvent(state.eventListeners, 'payment_approval_request', req);

            try {
              const approvalResult = await persistAndAuthoriseExpressPayment({
                expressApprovalRequest,
                shippingOptionId: (expressApprovalRequest.shippingOptionId ?? state.selectedShippingOptionId) || '',
                tenantCode,
                expressPaymentId,
                forterToken: forterToken ?? null
              });

              integrationService.setExpressPaymentApprovalResult(approvalResult);
            } catch (error: unknown) {
              if (error instanceof ESWError) {
                logger.error(`${error.type}:`, error.toJSON());
                triggerEvent(state.eventListeners, 'error', {
                  error,
                  component: 'express-payment'
                });
              } else {
                const wrappedError = new ESWStateError(
                  `Unexpected error in payment authorization: ${error instanceof Error ? error.message : String(error)}`,
                  AUTHORIZE_PAYMENT_FAILED
                );

                logger.error('Error while authorising express payment:', error instanceof Error ? error.message : String(error), error);
                triggerEvent(state.eventListeners, 'error', { error: wrappedError, component: 'express-payment' });
              }

              let reason = 'Payment authorization failed. Please try again.';
              if (error instanceof ESWPaymentError) {
                // Payment declined or validation failed - safe to show user
                reason = error.message;
              } else if (error instanceof ESWConnectionError) {
                reason = 'Network error. Please check your connection and try again.';
              } else if (error instanceof ESWAPIError) {
                reason = error.message;
              }

              integrationService.setExpressPaymentApprovalResult({
                approved: false,
                reason,
                authorizeRequest: {} as AuthorizeExpressPaymentRequestModel
              });
            }
          },
          error: createSubscriptionErrorHandler('Payment authorization')
        });

        const paymentSessionResultSubscription = integrationService.getPaymentSessionResult$.subscribe({
          next: (result: SessionResult) => {
            triggerEvent(state.eventListeners, 'payment_session_result', result);
          },
          error: createSubscriptionErrorHandler('Payment session result')
        });

        state = {
          ...state,
          integrationService,
          addressChangedSubscription,
          shippingOptionChangedSubscription,
          paymentAttemptSubscription,
          paymentSessionResultSubscription,
          setupPaymentAuthorisationSubscription
        };

        state = {
          ...state,
          mounted: true,
          element: newElement,
          container: newContainer
        };

        logger.info('Express payment panel mounted successfully to', selector);
      } catch (error: unknown) {
        // Abort any pending Angular initialization timeout/listeners
        angularAbortController.abort();

        cleanupResources();

        // Trigger error event for integrator before re-throwing
        if (error instanceof ESWError) {
          logger.error(`${error.type}:`, error.toJSON());
          triggerEvent(state.eventListeners, 'error', {
            error,
            component: 'express-payment'
          });
          throw error;
        }

        // Wrap unexpected errors in ESWStateError
        const wrappedError = new ESWStateError(
          `Failed to mount express payment panel: ${error instanceof Error ? error.message : String(error)}`,
          MOUNT_FAILED
        );

        logger.error('Failed to mount express payment panel:', error instanceof Error ? error.message : String(error), error);
        triggerEvent(state.eventListeners, 'error', {
          error: wrappedError,
          component: 'express-payment'
        });
        throw wrappedError;
      }
    },

    update: (updates: Pick<ExpressPaymentComponentConfig, 'language' | 'i18n' | 'userData'>): void => {
      if (!state.element) {
        const error = new ESWStateError(
          'Cannot update express payment panel: component not mounted. Call mount() first.',
          COMPONENT_NOT_MOUNTED
        );
        logger.error(error.message);
        triggerEvent(state.eventListeners, 'error', {
          error,
          component: 'express-payment'
        });
        throw error;
      }

      if (updates.userData) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state.element as any).userData = updates.userData;
      }

      if (updates.language || updates.i18n) {
        logger.warn('Language/i18n updates not yet implemented for express payment panel');
      }
    },

    on: <EventName extends ExpressPaymentOutputEvent>(
      eventName: EventName,
      callback: (data: ExpressPaymentEventMap[EventName]) => void
    ): void => {
      state = {
        ...state,
        eventListeners: {
          ...state.eventListeners,
          [eventName]: callback
        }
      };
    },

    unmount: (): void => {
      cleanupResources();
    }
  };
}
